/**
 * The sound that says somebody wants you.
 *
 * Two notes, synthesised here rather than shipped as a file. That is not
 * cleverness for its own sake: this app is served under a Content-Security-Policy
 * whose `media-src` is `'self' blob:`, so a `data:` URI would be refused outright
 * — and a generated Blob is admitted, costs no request, and cannot be a 404 after
 * a deploy. It also stays a couple of dozen lines of arithmetic instead of a
 * binary nobody can diff.
 *
 * It plays through an ordinary <audio> element rather than the Web Audio graph,
 * because that is the only way it can honour the speaker the user chose in
 * Settings: `setSinkId` lives on media elements, and an AudioContext has no
 * equivalent. Everything the media bar decides — the master level, the mute — is
 * applied here for the same reason, so the one button that means "stop making
 * noise" does.
 */
import { applyOutput, effectiveVolume, masterMuted, getSettings, canSetVolume } from './devices.js';

const SAMPLE_RATE = 44100;

/**
 * Two notes a fifth apart, the second overlapping the tail of the first: A5 into
 * E6. Short, soft, and pitched above speech so it carries over a conversation
 * without sounding like an alarm.
 */
const NOTES = [
  { hz: 880.0, start: 0, seconds: 0.1 },
  { hz: 1318.5, start: 0.075, seconds: 0.17 },
];

const LENGTH_SECONDS = 0.28;

/** Never twice in this many ms, however much arrives at once. */
const THROTTLE_MS = 1500;

/** Wraps 16-bit mono PCM in a WAV container. */
function encodeWav(samples) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const ascii = (offset, text) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(offset + i, text.charCodeAt(i));
  };

  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true); // the size of this chunk
  view.setUint16(20, 1, true); // uncompressed PCM
  view.setUint16(22, 1, true); // one channel
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, SAMPLE_RATE * 2, true); // bytes per second
  view.setUint16(32, 2, true); // bytes per frame
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (const sample of samples) {
    const clamped = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, Math.round(clamped * 32767), true);
    offset += 2;
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/**
 * Renders the chime.
 *
 * Each note gets a 5ms fade-in and an exponential decay. The fade-in is not
 * decoration: a sine wave that begins at full amplitude starts with a step, and
 * a step is a click.
 */
function render() {
  const total = Math.ceil(SAMPLE_RATE * LENGTH_SECONDS);
  const samples = new Float32Array(total);

  for (const note of NOTES) {
    const from = Math.floor(note.start * SAMPLE_RATE);
    const count = Math.floor(note.seconds * SAMPLE_RATE);
    for (let i = 0; i < count && from + i < total; i += 1) {
      const t = i / SAMPLE_RATE;
      const attack = Math.min(1, t / 0.005);
      const decay = Math.exp(-t / (note.seconds * 0.35));
      // Summed, so the overlap is a chord rather than a cut. 0.32 keeps the two
      // together inside the clipping limit.
      samples[from + i] += Math.sin(2 * Math.PI * note.hz * t) * attack * decay * 0.32;
    }
  }
  return samples;
}

let element = null;
let objectUrl = null;
let lastPlayed = 0;

/**
 * Builds the audio element, once.
 *
 * Deferred until something actually wants to make a sound, so a session that is
 * never mentioned never encodes anything.
 */
function audio() {
  if (element) return element;
  try {
    objectUrl = URL.createObjectURL(encodeWav(render()));
    element = new Audio(objectUrl);
    element.preload = 'auto';
  } catch {
    // No Audio, no Blob, no URL: there is simply no sound on this browser.
    element = null;
  }
  return element;
}

/** Whether the reader has asked for these at all. */
export function notificationSoundEnabled() {
  return getSettings().notificationSound !== false;
}

/**
 * Plays the chime, unless something says not to.
 *
 * `force` is for the Settings toggle, which plays it as it is switched on so the
 * choice can be heard being made — that one ignores the throttle and the setting
 * it is in the act of changing, but still respects a muted media bar.
 */
export function playNotification({ force = false } = {}) {
  if (!force && !notificationSoundEnabled()) return false;

  // The mute button in the media bar is the one control that means "stop making
  // noise"; a notification that talked over it would read as a bug.
  if (masterMuted()) return false;

  const now = Date.now();
  // Ten mentions arriving together are still one sound.
  if (!force && now - lastPlayed < THROTTLE_MS) return false;

  const sound = audio();
  if (!sound) return false;

  /*
   * Stamped here, after everything that could have stopped it, and not before:
   * a sound that was suppressed has not been heard, so it must not spend the
   * budget. Counting it earlier meant a mention arriving while muted silenced
   * the next one too, for a second and a half after the mute came off.
   */
  lastPlayed = now;

  // Routed to the chosen speaker, and levelled with everything else. Both are
  // best-effort: a browser that refuses either still plays the sound.
  applyOutput(sound).catch(() => {});
  if (canSetVolume()) sound.volume = effectiveVolume(0.55);

  try {
    sound.currentTime = 0;
  } catch {
    /* not seekable yet; it will play from the start anyway */
  }
  // Autoplay policy can refuse this until the page has been interacted with.
  // By the time anyone is mentioned they have clicked their way into a room, so
  // this is a formality - but an unhandled rejection is still noise in the log.
  Promise.resolve(sound.play()).catch(() => {});
  return true;
}

/** Releases the generated sound. Used by the tests; harmless in the browser. */
export function releaseNotification() {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrl = null;
  element = null;
  lastPlayed = 0;
}

export default { playNotification, notificationSoundEnabled, releaseNotification };
